import { PropsWithChildren, createContext, useContext, useState } from 'react'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { DebouncedSearch, useDebouncedSearch } from 'modules/core/hooks/useSearch'
import { track, trackingEvents } from 'modules/core/lib/Tracking'

import { locale } from 'modules/marketplace/components/utils/locales'
import { useGetMarketplaceInstallations } from 'modules/marketplace/hooks/integrations/useGetMarketplaceInstallations'
import { useGetMarketplaceIntegrations } from 'modules/marketplace/hooks/integrations/useGetMarketplaceIntegrations'

import { Category, IntegrationsCatalogSorter } from './integrationCatalogSorter'

const IntegrationsCatalogContext = createContext<{
  search: DebouncedSearch
  installedFilter: boolean
  installedFilterToggle: (value: boolean) => void
  categoryFilter: string[]
  filterByCategory: (categoryFilter?: string[]) => void
  countryFilter: string[]
  filterByCountry: (countryFilter?: string[]) => void
  categories: Category[]
  filteredCategories: Category[]
  loading: boolean
  installedIntegrations: string[]
} | null>(null)

const integrationCatalogSorter = new IntegrationsCatalogSorter()

export const IntegrationsCatalogProvider = ({ children }: PropsWithChildren) => {
  const search = useDebouncedSearch()
  const [installedFilter, setInstalledFilter] = useState(false)
  const [categoryFilter, setCategoryFilter] = useState<string[]>([])
  const [countryFilter, setCountryFilter] = useState<string[]>([])
  const [filteredCategories, setFilteredCategories] = useState<Category[]>([])

  const {
    access: { locale: user_locale },
    company: { id: companyId },
  } = useCurrentSession()
  const datoFormatLocale = locale[user_locale]

  const { data: integrationsData, loading: integrationsLoading } = useGetMarketplaceIntegrations({
    locale: datoFormatLocale,
  })

  const { data: installationsData, loading: installationsLoading } = useGetMarketplaceInstallations(
    {
      companyIds: [companyId],
    }
  )

  const installedIntegrations = installationsData?.map((installation) => installation.integrationId)
  const categories = integrationsData.reduce<Category[]>((memo, integration) => {
    const categoryId = integration.category.id
    const value = memo.find((category) => category.id === categoryId)

    if (value) {
      value.children.push(integration)
    } else {
      memo.push({
        id: categoryId,
        title: integration.category.title,
        description: integration.category.description,
        children: [integration],
      })
    }

    return memo
  }, [])

  useEffect(() => {
    integrationCatalogSorter.setCategories(categories)
  }, [categories.length])

  useEffect(() => {
    integrationCatalogSorter.setInstalledIntegrations(installedIntegrations ?? [])
  }, [installedIntegrations.length])

  useEffect(() => {
    integrationCatalogSorter.setSearchValue(search.debouncedSearch)
    setFilteredCategories(integrationCatalogSorter.filterCategories())
  }, [
    search.debouncedSearch,
    installedFilter,
    categoryFilter.length,
    countryFilter.length,
    categories.length,
  ])

  const installedFilterToggle = (value: boolean) => {
    integrationCatalogSorter.setInstalledFilter(value)
    setInstalledFilter(value)
    const status = value ? 'enabled' : 'disabled'
    track(trackingEvents.marketplace.filterMarketplaceIntegrationsByInstalledStatus, {
      installed: status,
    })
  }

  const filterByCategory = (selectedCategoryIds: string[] = []) => {
    integrationCatalogSorter.setCategoryFilter(selectedCategoryIds)
    setCategoryFilter(selectedCategoryIds)
    track(trackingEvents.marketplace.filterByCategory, {
      selected_categories: selectedCategoryIds,
    })
  }

  const filterByCountry = (selectedCountryIds: string[] = []) => {
    integrationCatalogSorter.setCountryFilter(selectedCountryIds)
    setCountryFilter(selectedCountryIds)
    track(trackingEvents.marketplace.filterByCountry, {
      selected_countries: selectedCountryIds,
    })
  }

  return (
    <IntegrationsCatalogContext.Provider
      value={{
        search,
        installedFilter,
        installedFilterToggle,
        categoryFilter,
        filterByCategory,
        countryFilter,
        filterByCountry,
        filteredCategories,
        categories,
        loading: integrationsLoading || installationsLoading,
        installedIntegrations,
      }}
    >
      {children}
    </IntegrationsCatalogContext.Provider>
  )
}

export function useIntegrationsCatalog() {
  const currentIntegrationsCatalogContext = useContext(IntegrationsCatalogContext)

  if (!currentIntegrationsCatalogContext) {
    throw new Error('useIntegrationsCatalog must be used within a IntegrationsCatalogProvider')
  }

  return currentIntegrationsCatalogContext
}
